import { defineStore } from 'pinia'
import * as invoiceApi from '../services/api/invoice'

export interface IInvoiceData {
  id: number
  note: string
  amount: number
  type: string
  status: string
  method: string
  is_urgent: boolean
  scheduled_at: string
  assignee_id: number
  receiver: any
  customer: any
  assignee: any
  order: any
  created_at: string
  payer_id: number
}

export const useInvoiceStore = defineStore('invoice', {
  state: () => ({
    invoice: {
      id: 0,
      note: '',
      amount: 0,
      type: '',
      status: '',
      method: '',
      is_urgent: false,
      scheduled_at: '',
      receiver: {
        bank_info: '',
        phone: '',
        bank_name: '',
        bank_no: '',
      },
      assignee_id: 0,
      customer: null,
      assignee: null,
      order: null,
      created_at: '',
      payer_id: 0,
    } as IInvoiceData,
    isLoading: false,
  }),

  actions: {
    setInvoice(data: IInvoiceData) {
      this.invoice = data
    },

    setLoading(value: boolean) {
      this.isLoading = value
    },

    async getInvoice(invoice_id: number) {
      try {
        this.setLoading(true)
        const { data } = await invoiceApi.get(invoice_id)
        this.setInvoice(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async updateInvoice(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await invoiceApi.update(this.invoice.id, payload)
        this.setInvoice(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async applyInvoice() {
      try {
        this.setLoading(true)
        const { data } = await invoiceApi.apply(this.invoice.id)
        this.setInvoice(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async rejectInvoice(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await invoiceApi.reject(this.invoice.id, payload)
        this.setInvoice(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async remakeInvoice(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await invoiceApi.remake(this.invoice.id, payload)
        this.setInvoice(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async approveInvoice(params: any) {
      try {
        this.setLoading(true)
        const { data } = await invoiceApi.approve(this.invoice.id, params)
        this.setInvoice(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async payInvoice(params: any) {
      try {
        this.setLoading(true)
        const { data } = await invoiceApi.pay(this.invoice.id, params)
        this.setInvoice(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },
  },
})

export const useInvoiceListStore = defineStore('invoiceList', {
  state: () => ({
    invoices: [] as IInvoiceData[],
    count: 0,
    isLoading: false,
    total: 0,
  }),

  actions: {
    setCount(data: number) {
      this.count = data
    },

    setInvoices(data: IInvoiceData[]) {
      this.invoices = data
    },

    setLoading(value: boolean) {
      this.isLoading = value
    },

    setTotal(value: number) {
      this.total = value
    },

    async countInvoices() {
      try {
        this.setLoading(true)
        const { data } = await invoiceApi.count()
        this.setCount(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listInvoices(params: any) {
      try {
        this.setLoading(true)
        const result = await invoiceApi.list(Object.assign(params, { type: 'order_payment' }))
        const { data, total } = result as any
        this.setInvoices(data)
        this.setTotal(total)
        this.setLoading(false)
        return result
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listAllInvoices(params: any) {
      try {
        this.setLoading(true)
        const result = await invoiceApi.listAllByDirector(Object.assign(params, { type: 'order_payment' }))
        const { data, total } = result as any
        this.setInvoices(data)
        this.setTotal(total)
        this.setLoading(false)
        return result
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },
  },
})
